import { CircleHelp, WandSparkles } from "lucide-react";
import { useEffect, useState } from "react";
import { DetailWindow } from "../DetailWindow";
import { type DamageElement, type Monster, type RoomView } from "../types";
import { AttackElementMark } from "./Elements";
import { formatDisplayNumber, formatTraitValue } from "./format";
import { spriteForRaidBoss } from "./gameArt";

export function EnemyDetail({
  name,
  image,
  resistance,
  weakness,
  stats,
  traits,
  onClose,
}: {
  name: string;
  image?: string;
  resistance: DamageElement;
  weakness: DamageElement;
  stats: Array<{ label: string; value: string }>;
  traits: string[];
  onClose: () => void;
}) {
  return (
    <DetailWindow
      className="enemy-detail-window"
      labelledBy="enemy-detail-title"
      onClose={onClose}
      header={<h2 id="enemy-detail-title">{name}</h2>}
    >
      <div className="enemy-detail-overview">
        {image && <img src={image} alt={name} />}
        <div>
          <small>战斗进行中</small>
          <div className="enemy-element">
            <span>抵抗</span>
            <AttackElementMark element={resistance ?? "neutral"} />
          </div>
          <div className="enemy-element">
            <span>弱点</span>
            <AttackElementMark element={weakness ?? "neutral"} />
          </div>
        </div>
      </div>
      <dl className="enemy-detail-stats">
        {stats.map((stat) => (
          <div key={stat.label}>
            <dt>{stat.label}</dt>
            <dd>{stat.value}</dd>
          </div>
        ))}
      </dl>
      <section className="enemy-detail-traits">
        <h3>特性与机制</h3>
        {traits.length > 0 ? (
          traits.map((trait, index) => (
            <p key={index}>
              <WandSparkles size={16} />
              <span>{trait}</span>
            </p>
          ))
        ) : (
          <p>无额外特性</p>
        )}
      </section>
    </DetailWindow>
  );
}

export function RoomEnemyInspector({ room }: { room: RoomView["room"] }) {
  const [open, setOpen] = useState(false);
  useEffect(() => setOpen(false), [room.id, room.duoDepth, room.status]);
  const boss = room.mode === "raid" ? room.boss : undefined;
  const name = boss?.name ?? "矿道敌群";
  return (
    <>
      <button
        className="enemy-inspect-target"
        aria-label={`查看${name}详情`}
        title={`查看${name}详情`}
        aria-haspopup="dialog"
        onClick={() => setOpen(true)}
      >
        <CircleHelp size={15} />
      </button>
      {open && (
        <EnemyDetail
          name={name}
          image={boss ? spriteForRaidBoss(boss.id) : undefined}
          resistance={boss?.resistanceElement ?? room.duoResistanceElement ?? "neutral"}
          weakness={boss?.weaknessElement ?? room.duoWeaknessElement ?? "neutral"}
          onClose={() => setOpen(false)}
          stats={
            boss
              ? [
                  { label: "生命", value: `${boss.hp} / ${boss.maxHp}` },
                  { label: "阶段", value: String(boss.phase) },
                  {
                    label: "伤害减免",
                    value: `${formatDisplayNumber(boss.damageReduction * 100)}%`,
                  },
                  { label: "攻击次数", value: String(boss.attackCount) },
                ]
              : [
                  {
                    label: "生命",
                    value: `${room.duoMonsterHp ?? 0} / ${room.duoMonsterMaxHp ?? 0}`,
                  },
                  { label: "深度", value: String(room.duoDepth ?? 0) },
                ]
          }
          traits={boss ? [boss.description, room.mutation.description].filter(Boolean) : []}
        />
      )}
    </>
  );
}

export function monsterTraitSummary(monster: Monster, reflectCapMultiplier = 2): string {
  const primary = monsterTraitDetail(monster.trait, monster.traitValue, reflectCapMultiplier);
  const extra = monsterTraitDetail(
    monster.extraTrait,
    monster.extraTraitValue,
    reflectCapMultiplier,
  );
  const primaryLabel =
    primary && monster.boss && monster.phase < 2 ? `二阶段：${primary}` : primary;
  const elite = (monster.eliteTraits ?? []).map((entry) =>
    monsterTraitDetail(entry.id, entry.value, reflectCapMultiplier),
  );
  return [primaryLabel, extra && `深层：${extra}`, ...elite]
    .filter((detail): detail is string => Boolean(detail))
    .join("；");
}

export function monsterTraitDetail(
  trait?: string,
  value?: number,
  reflectCapMultiplier = 2,
): string {
  const amount = Math.max(1, Math.round(value ?? 1));
  const multiplier = formatTraitValue(value ?? 1);
  const percent = Math.round((value ?? 0) * 100);
  switch (trait) {
    case "fast_atb":
      return `急袭：ATB 周期 x${multiplier}`;
    case "high_armor":
      return `硬壳：护甲 x${multiplier}`;
    case "board_debuff":
      return `雾遮：攻击遮蔽 ${amount} 格 3 秒`;
    case "block_decay":
      return `蚀盾：攻击额外削减 ${percent}% 当前格挡`;
    case "on_death_swarm":
      return `分裂：死亡生成 ${amount} 只弱体`;
    case "lock_row":
      return "缠根：攻击锁 1 行 4 秒，剑三连解除";
    case "mana_burn":
      return `灼法：攻击烧毁 ${amount} 法力`;
    case "reflect":
      return `反震：反弹 ${percent}% 物理伤害，单次上限 ${formatDisplayNumber(reflectCapMultiplier, true)}×怪物 ATK`;
    case "burn_dot":
      return `灼烧：命中后每秒 ${amount} 真伤，持续 3 秒`;
    case "tile_corrupt":
      return `烧结：攻击将 ${amount} 个木方块变矿`;
    case "revive_once":
      return `复生：首次死亡以 ${percent}% HP 复活`;
    case "summon":
      return `唤从：每 12 秒召唤 ${amount} 只弱体`;
    case "mirror_slide":
      return "回响：每次滑动随之移 1 格，同列时范围伤害";
    case "skill_lock":
      return `沉默：封锁 1 个技能槽 ${amount} 秒`;
    case "armor_ignore_block":
      return `穿格：${percent}% 伤害穿透格挡`;
    case "resource_steal":
      return `掠采：偷走本遭遇已获资源的 ${percent}%`;
    case "enrage_below_hp":
      return `狂乱：HP 低于 ${percent}% 时 ATB 周期 x0.6`;
    case "reweave_board":
      return `织盘：每 ${amount} 秒随机重排 2 行`;
    default:
      return trait ? `未知特性：${trait}` : "";
  }
}

export function raidCueRate(trait: string): number {
  switch (trait) {
    case "crystal_shield":
      return 0.78;
    case "drain_ritual":
      return 0.66;
    case "vein_mark":
      return 1.28;
    case "collapse":
      return 0.56;
    default:
      return 1;
  }
}
